"use client";

import React, { useMemo, useState } from "react";
import Link from "next/link";
import { RssIcon } from "lucide-react";
import { cn } from "@/utils";
import Section from "@/components/ui/section";
import { Button } from "@/components/ui/button";
import { RSSDialog } from "@/components/ui/rss-dialog";
import { absoluteUrl } from "@/lib/seo/site";
import {
  type RecentAdvisory,
  formatAdvisoryDisclosureDate,
  formatAdvisoryTickerLabel,
  getAdvisoryHref,
} from "@/lib/recent-advisories";

const SEPARATOR = ":";
const SECONDS_PER_ITEM = 14;
const MIN_TICKER_DURATION_SECONDS = 120;

interface AdvisoryTickerProps {
  advisories: RecentAdvisory[];
  className?: string;
}

export default function AdvisoryTicker({
  advisories,
  className,
}: AdvisoryTickerProps) {
  const [rssDialogOpen, setRssDialogOpen] = useState(false);

  const items = useMemo(
    () =>
      advisories.map((advisory) => {
        const id = advisory.advisoryId || advisory.cveId || null;
        const date = formatAdvisoryDisclosureDate(advisory.publishDate);

        return {
          key: advisory.id,
          href: getAdvisoryHref(advisory.slug),
          id,
          date,
          dateTime: advisory.publishDate,
          label: formatAdvisoryTickerLabel(advisory),
          fullLabel: [
            date,
            formatAdvisoryTickerLabel(advisory, { truncateTitle: false }),
          ]
            .filter(Boolean)
            .join(` ${SEPARATOR} `),
        };
      }),
    [advisories]
  );

  if (items.length === 0) return null;

  const loopItems = [...items, ...items];
  const tickerDurationSeconds = Math.max(
    items.length * SECONDS_PER_ITEM,
    MIN_TICKER_DURATION_SECONDS
  );

  const tickerBar = (
    <Section
      id="latest-advisories"
      aria-label="Recent security advisories"
      wrapperClassName={cn(
        "!py-0 overflow-hidden border-t border-border/40 bg-background/90 backdrop-blur-md",
        className
      )}
      className="flex items-center gap-0 py-2 md:py-2.5"
    >
      <Link
        href="/advisories"
        className="relative z-20 shrink-0 self-center bg-background pr-4 md:pr-5 text-xs text-balance md:text-sm text-muted-foreground font-mono uppercase tracking-wider transition-colors hover:text-muted-foreground-light select-none after:pointer-events-none after:absolute after:left-full after:top-0 after:h-full after:w-6 after:bg-gradient-to-r after:from-background after:to-transparent lg:after:w-24"
      >
        <span className="lg:hidden">Advisories</span>
        <span className="hidden lg:inline">Latest Advisories</span>
      </Link>

      <div className="advisory-ticker-fade relative min-w-0 flex-1 overflow-hidden pl-2 md:pl-3">
        <div
          className="advisory-ticker-track flex w-max items-center"
          style={
            items.length > 1
              ? {
                animation: `advisory-ticker ${tickerDurationSeconds}s linear infinite`,
              }
              : undefined
          }
        >
          {loopItems.map((item, index) => (
            <React.Fragment key={`${item.key}-${index}`}>
              {index > 0 && (
                <span
                  className="mx-4 md:mx-6 shrink-0 text-border-light"
                  aria-hidden
                >
                  |
                </span>
              )}
              <Link
                href={item.href}
                title={
                  item.fullLabel !== item.label ? item.fullLabel : undefined
                }
                className="inline-flex max-w-[min(28rem,48vw)] min-w-0 items-center gap-2 text-sm text-muted-foreground-light transition-colors hover:text-white"
              >
                {item.date ? (
                  <time
                    dateTime={item.dateTime}
                    className="shrink-0 font-mono text-sm uppercase tracking-wide text-inherit transition-colors [font-feature-settings:'zero'_on] text-primary-200"
                  >
                    {item.date}
                  </time>
                ) : null}
                {item.date && (item.id || item.label) && (
                  <span
                    className="shrink-0 text-inherit opacity-60 transition-[opacity,color]"
                    aria-hidden
                  >
                    {SEPARATOR}
                  </span>
                )}
                <span className="min-w-0 truncate text-inherit transition-colors">
                  {item.label}
                </span>
              </Link>
            </React.Fragment>
          ))}
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-background to-transparent lg:w-14"
          aria-hidden
        />
      </div>

      <Button
        variant="link"
        size="xs"
        aria-label="Stay up to date"
        className="relative z-20 shrink-0 !h-auto !min-h-0 !py-0 self-center bg-background pl-4 md:pl-5 text-xs md:text-sm text-muted-foreground hover:!text-secondary-500 hover:!decoration-secondary-500 before:pointer-events-none before:absolute before:right-full before:top-0 before:h-full before:w-6 before:bg-gradient-to-l before:from-background before:to-transparent lg:before:w-24"
        onClick={() => setRssDialogOpen(true)}
      >
        <RssIcon className="size-4 text-secondary-500" />
        <span className="hidden sm:inline">Stay up to date</span>
      </Button>
    </Section>
  );

  return (
    <>
      <div className="h-10 md:h-11 shrink-0" aria-hidden />
      <div className="fixed inset-x-0 bottom-0 z-40">{tickerBar}</div>
      <RSSDialog
        open={rssDialogOpen}
        onOpenChange={setRssDialogOpen}
        rssUrl={absoluteUrl("/advisories/rss.xml")}
      />
    </>
  );
}
